import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { faChartSimple, faFilePdf, faNetworkWired } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import {
	MockupWindow,
	BoardMockup,
	PlanMockup,
	TeamMockup,
	AssistantMockup,
} from "@/pages/Public/components/ProductMockups";
import { trackCheckCtaClick, trackDemoRequestClick } from "@/utils/analytics";
import { ROUTE_CONFIG } from "@/routes/config";

type Module = {
	kicker: string;
	title: string;
	description: string;
};

type ExtraModule = {
	title: string;
	description: string;
};

const MODULE_VISUALS: ReactNode[] = [
	<PlanMockup key="plan" />,
	<BoardMockup key="board" />,
	<TeamMockup key="team" />,
	<AssistantMockup key="assistant" />,
];

const EXTRA_MODULE_ICONS = [faChartSimple, faFilePdf, faNetworkWired];

export default function Product() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const ts = (key: string) => t(`marketing.product.${key}`);

	const modules = t("marketing.product.modules", { returnObjects: true }) as Module[];
	const extraModules = t("marketing.product.extraModules", {
		returnObjects: true,
	}) as ExtraModule[];

	const handleStartCheck = () => {
		trackCheckCtaClick("product_page");
		navigate("/questionnaire/yes");
	};

	const handleDemoRequest = () => {
		trackDemoRequestClick("product_page");
		navigate(ROUTE_CONFIG.CONTACT);
	};

	return (
		<div className="w-full flex flex-col items-center font-urbanist">
			{/* Page hero */}
			<section className="w-full bg-surface py-14 sm:py-20 px-4 flex justify-center">
				<div className="max-w-[1420px] w-full flex flex-col items-center text-center gap-6">
					<h1 className="font-bold text-ink text-[36px] md:text-[48px] leading-[120%] max-w-[900px]">
						{ts("title")}
					</h1>
					<p className="font-medium text-subtle text-[18px] md:text-[20px] leading-[150%] max-w-[820px]">
						{ts("subtitle")}
					</p>
				</div>
			</section>

			{/* Module deep-dives */}
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-16 sm:gap-24">
					{modules?.map((module, index) => (
						<div
							key={module.title}
							className={`flex flex-col gap-10 lg:gap-20 items-center ${
								index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
							}`}
						>
							<div className="flex flex-col gap-4 w-full lg:w-1/2 max-w-[560px]">
								<p className="text-[14px] font-bold uppercase tracking-[0.08em] text-accent-strong">
									{module.kicker}
								</p>
								<h2 className="text-[28px] md:text-[32px] font-semibold leading-[125%] text-ink">
									{module.title}
								</h2>
								<p className="text-[18px] font-normal leading-[150%] text-subtle">
									{module.description}
								</p>
							</div>
							<div className="w-full lg:w-1/2 max-w-[620px]">
								<MockupWindow>{MODULE_VISUALS[index]}</MockupWindow>
							</div>
						</div>
					))}
				</div>
			</section>

			{/* Additional modules */}
			<section className="w-full px-4 md:px-16 bg-surface">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col gap-12">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-ink text-center">
						{ts("extraModulesTitle")}
					</h2>
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8">
						{extraModules?.map((module, index) => (
							<div
								key={module.title}
								className="flex flex-col gap-4 bg-white border border-primary-gray-lightest rounded-lg px-6 py-6"
							>
								<div className="bg-accent-tint flex w-12 h-12 rounded-md justify-center items-center">
									<Icon className="w-5 h-5 text-primary-blue" icon={EXTRA_MODULE_ICONS[index]} />
								</div>
								<h3 className="text-[20px] font-bold text-ink">{module.title}</h3>
								<p className="text-subtle text-[16px] leading-[150%]">{module.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* Closing CTA */}
			<section className="w-full px-4 md:px-16">
				<div className="max-w-[1420px] mx-auto py-16 sm:py-24 flex flex-col items-center text-center gap-8">
					<h2 className="text-[32px] md:text-[40px] font-semibold leading-[120%] text-ink max-w-[820px]">
						{ts("ctaTitle")}
					</h2>
					<p className="text-[18px] leading-[150%] text-subtle max-w-[700px]">
						{ts("ctaDescription")}
					</p>
					<div className="flex flex-col sm:flex-row gap-4">
						<button
							className="px-10 py-3 rounded-button bg-accent hover:bg-accent-dark transition-colors text-white text-[16px] font-bold"
							type="button"
							onClick={handleStartCheck}
						>
							{ts("ctaCheckButton")}
						</button>
						<button
							className="px-10 py-3 rounded-xl bg-white border border-primary-gray-lightest hover:bg-surface transition-colors text-ink text-[16px] font-medium"
							type="button"
							onClick={handleDemoRequest}
						>
							{ts("ctaDemoButton")}
						</button>
					</div>
				</div>
			</section>
		</div>
	);
}
